import React, { useEffect, useContext, useCallback } from 'react'
import SVG from 'react-inlinesvg'
import { motion, AnimatePresence } from 'framer-motion'
import { useCookies } from 'react-cookie'

import { GeolocalizationContext } from '../../../utils/contexts/geolocalization-context'
import Image from '../../parts/image'
import AgeGateHeader from './age-gate-header'
import AgeGateForm from './age-gate-form'
import AgeGateCero from './age-gate-cero'
import defaultCloseIcon from '../../../assets/icons/close.svg'

/**
 * Display an age gating form to prevent underage users to access specific pages
 */
const AgeGate = ({ dataID, data, type, strings, dateFormat, closeIcon, fullPage, pageContext, eventSection }) => {
  const { ageGate, rating, legalAge, formSubmit } = useContext(GeolocalizationContext)
  const [gameRating, setGameRating] = rating
  const [ageGateSettings, setAgeGateSettings] = ageGate
  const [formSubmitted, setFormSubmitted] = formSubmit
  const [isLegalAge, setLegalAge] = legalAge
  const [cookies, setCookie] = useCookies()
  const lockCookieName = `${process.env.GATSBY_APP_SHORT_NAME ? process.env.GATSBY_APP_SHORT_NAME : 'bhvr'}-underage-lock`
  const fullPageEnabled = data?.pageEnabled || ageGateSettings?.fullPage || fullPage ? true : false

  // Set Age Gate Strings and define default values if not passed from parent component
  // TODO: Default strings should be located in a JSON
  let stringsErrors = strings.errors
  if (stringsErrors === null) {
    stringsErrors = {
      dayError: "Day not recognized.",
      dayErrorDesc: "Enter a day between 1 and 31.",
      monthError: "Month not recognized.",
      monthErrorDesc: "Enter a month between 1 and 12.",
      yearError: "Year not recognized.",
      yearErrorDesc: "Enter a year between 1900 and 2022."
    }
  }
  let stringsDates = strings.dates
  if (stringsDates === null) {
    stringsDates = {
      day: "Day",
      month: "Month",
      year: "Year",
      DD: "DD",
      MM: "MM",
      YYYY: "YYYY",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds"
    }
  }

  // Disable Age Gate if user meet the requirements or if age gate has already been filled
  useEffect(() => {
    if (!data.siteEnabled || cookies[data.cookieName] || !ageGateSettings.enabled) {
      setLegalAge(true)
      setAgeGateSettings(prev => ({ ...prev, displayed: false, usage: null }))
    }
  }, [])

  // Display Page Overlay
  useEffect(() => {
    if (!data.isPopup && !isLegalAge && fullPageEnabled && !cookies[data.cookieName] && ageGateSettings.enabled) {
      setAgeGateSettings(prev => ({ ...prev, displayed: true, usage: 'globalPageGate' }))
    } else {
      setAgeGateSettings(prev => ({ ...prev, displayed: false, usage: null }))
    }
  }, [data])

  // Setup the Age gate popup type (Page Gate or Video Gate)
  let typeData = data
  if (type && (type === 'video' || type === 'modal')) {
    typeData = data.video ? data.video : data
  }
  let classes = typeData.classes

  // Close popup (if it is "cancellable")
  const cancellable = type && type === 'modal' ? false : typeData.cancellable
  const close = () => {
    setAgeGateSettings(prev => ({ ...prev, displayed: false, usage: null }))
  }
  const handleEscape = useCallback(event => {
    if (event.keyCode === 27 && cancellable) {
      close()
    }
  }, [close])

  useEffect(() => {
    if (ageGateSettings.displayed) {
      document.addEventListener('keydown', handleEscape, false)
    }
    return () => {
      document.removeEventListener('keydown', handleEscape, false)
    }
  }, [handleEscape, ageGateSettings.displayed])

  return (
    <AnimatePresence>
      {!cookies[data.cookieName] && ageGateSettings.displayed && (dataID === ageGateSettings.usage) && (
        <motion.div
          initial={{ opacity: `${dataID === 'globalPageGate' ? 1 : 0}` }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.5,
          }}
          className={`age-gate ${fullPageEnabled ? 'age-gate-fullpage' : ''} age-gate-${gameRating?.code} ${classes && classes.modal ? classes.modal : 'fixed top-0 z-god flex h-screen w-screen justify-center sm:items-center left-0 bg-black/90 blurry-background'}`}
          data-open={ageGateSettings.displayed}
          aria-modal={ageGateSettings.displayed}
          tabIndex={0}
        >
          {data.backgroundImage && (
            <Image
              media={data.backgroundImage}
              className="age-gate-background inset-0 absolute z-20 w-full h-full object-cover"
            />
          )}
          {cancellable && cancellable === true ? (
            <div
              className={`age-gate-close z-under cursor-pointer gatsby-hidden-mobile ${(classes && classes.backdrop !== null) ? classes.backdrop : ''}`}
              onClick={close}
            />
          ) : (
            <div
              className={`age-gate-close z-under ${(classes && classes.backdrop !== null) ? classes.backdrop : ''}`}
            />
          )}
          <div
            className={`age-gate-content ${classes && classes.content ? classes.content : 'absolute z-20 bg-black'}`}
          >
            {cancellable && (
              <button
                className="button-close duration-2500 absolute -right-16 -top-12 z-10 mt-2 flex h-14 w-14 items-center justify-center transition-all"
                aria-label="close"
                onClick={close}
              >
                <SVG
                  onError={error => console.log(error.message)}
                  cacheRequests={true}
                  preProcessor={code =>
                    code.replace(/fill=".*?"/g, 'fill="currentColor"')
                  }
                  src={closeIcon ? closeIcon : defaultCloseIcon}
                  className="absolute z-10 h-5 w-5"
                />
              </button>
            )}
            {gameRating?.code !== 'cero' && (
              <>
                <AgeGateHeader
                  data={data}
                  type={type}
                  locked={cookies[lockCookieName] || (formSubmitted && !isLegalAge) ? true : false}
                />
                <AnimatePresence>
                  {!cookies[lockCookieName] && (
                    !formSubmitted && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 0.5,
                        }}
                      >
                        <AgeGateForm
                          content={data}
                          type={type}
                          classes={classes}
                          dates={stringsDates}
                          errors={stringsErrors}
                          dateFormat={dateFormat}
                          lockCookieName={lockCookieName}
                          pageContext={pageContext}
                          eventSection={eventSection}
                        />
                      </motion.div>
                    )
                  )}
                  {(cookies[lockCookieName] || formSubmitted) && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        delay: 0.5,
                        duration: 0.5,
                      }}
                    >
                      {!isLegalAge && (
                        <h3 className="text-white">
                          {data.errorMessage}
                        </h3>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
            {gameRating?.code === 'cero' && (
              <AgeGateCero
                content={data}
                locked={cookies[lockCookieName] || (formSubmitted && !isLegalAge) ? true : false}
                lockCookieName={lockCookieName}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence >
  )
}
export default AgeGate